function getProductFormLocationPreferences() {
  return window.SDG?.LocationPreferences || null;
}

const initialLocationPreferences = getProductFormLocationPreferences();

if (initialLocationPreferences && typeof initialLocationPreferences.ensureInitialized === 'function') {
  try {
    initialLocationPreferences.ensureInitialized();
  } catch (error) {
    console.warn('product-form: failed to initialize location preferences', error);
  }
}

/**
 * Read the cached store selection, defaulting to an empty object.
 */
function getSelectedStoreLocation() {
  const locationPreferences = getProductFormLocationPreferences();
  if (!locationPreferences || typeof locationPreferences.getStoreLocationSync !== 'function') {
    return {};
  }
  return locationPreferences.getStoreLocationSync() || {};
}

/**
 * Persist the chosen store via the shared cart-attribute helper.
 */
async function persistSelectedStoreLocation(data) {
  const locationPreferences = getProductFormLocationPreferences();
  if (!locationPreferences || typeof locationPreferences.setStoreLocation !== 'function') return null;
  try {
    return await locationPreferences.setStoreLocation(data);
  } catch (error) {
    console.error('product-form: failed to persist store location', error);
    return null;
  }
}

function hasCompletePickupStore(store) {
  return Boolean(
    store?.locationId
    && store?.locationName
    && store?.locationHandle,
  );
}

function mapStockistLocationToStoreSelection(location) {
  if (!location || typeof location !== 'object') return null;

  const customFields = Array.isArray(location.custom_fields) ? location.custom_fields : [];
  const storeId = customFields.find((field) => field?.name === 'ID')?.value;
  const metaobjectHandle = customFields.find((field) => field?.name === 'Store MetaObject Handle')?.value;

  if (!location.name || !storeId || !metaobjectHandle) return null;

  return {
    locationName: location.name,
    locationLatitude: location.latitude,
    locationLongitude: location.longitude,
    locationId: storeId,
    locationHandle: metaobjectHandle,
  };
}

async function resolvePickupStoreOpenDaysForStore(store) {
  const locationPreferences = getProductFormLocationPreferences();
  if (!locationPreferences || typeof locationPreferences.resolvePickupStoreOpenDaysForStore !== 'function') {
    throw new Error('product-form: pickup-days resolver is unavailable');
  }
  return locationPreferences.resolvePickupStoreOpenDaysForStore(store);
}

/**
 * Find the relevant fulfillment picker context and read current inventory values.
 * @param {object} params
 * @param {Element|null|undefined} params.atcModal
 * @param {Element} params.hostEl
 * @returns {{ storeQty: number, transferDcQty: number }}
 */
function getInventoryContextFromDom({ atcModal, hostEl }) {
  const $context = atcModal
    || hostEl.closest('.js-product-info-container')
    || hostEl.closest('section')
    || document;

  const $picker = $context.querySelector('.js-fulfillment-picker');
  const storeQty = Number($picker?.dataset?.selectedStoreQty ?? 0) || 0;
  const transferDcQty = Number($picker?.dataset?.transferDcQty ?? 0) || 0;

  return { storeQty, transferDcQty };
}

/**
 * Normalize a release date string to YYYY-MM-DD.
 * @param {unknown} value
 * @returns {string}
 */
function normalizeReleaseDateIso(value) {
  const raw = typeof value === 'string' ? value.trim() : String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : '';
}

/**
 * Check whether an ISO date is after the shopper's local "today".
 * @param {string} isoDate
 * @returns {boolean}
 */
function isFutureReleaseDateIso(isoDate) {
  const normalizedIsoDate = normalizeReleaseDateIso(isoDate);
  if (!normalizedIsoDate) return false;

  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return normalizedIsoDate > todayIso;
}

/**
 * Resolve release-date ISO from the picker matching the submitted variant.
 * @param {object} params
 * @param {Element} params.hostEl
 * @param {string} params.variantId
 * @returns {string}
 */
function getReleaseDateIsoFromDom({ hostEl, variantId }) {
  if (!variantId) return '';

  const $context = hostEl.closest('.js-product-info-container')
    || hostEl.closest('section')
    || document;
  const $picker = $context?.querySelector?.(`.js-fulfillment-picker[data-variant-id="${variantId}"]`);

  return normalizeReleaseDateIso($picker?.dataset?.releaseDateIso || '');
}

/**
 * Keep the hidden preorder release-date property in sync before /cart/add.
 * Uses fulfillment-picker data when available; otherwise falls back to form input.
 * @param {FormData} formData
 * @param {object} params
 * @param {Element} params.hostEl
 */
function syncReleaseDateProperty(formData, { hostEl }) {
  if (!formData || typeof formData.set !== 'function' || typeof formData.delete !== 'function') return;

  const variantId = String(formData.get('id') || '').trim();
  const releaseDateFromDom = getReleaseDateIsoFromDom({ hostEl, variantId });
  const releaseDateFromForm = normalizeReleaseDateIso(formData.get('properties[_release_date_iso]'));
  const releaseDateIso = releaseDateFromDom || releaseDateFromForm;

  if (isFutureReleaseDateIso(releaseDateIso)) {
    formData.set('properties[_release_date_iso]', releaseDateIso);
    return;
  }

  formData.delete('properties[_release_date_iso]');
}

window.syncReleaseDateProperty = syncReleaseDateProperty;

/**
 * Parse requested quantity from a FormData object.
 * @param {FormData} formData
 * @returns {number}
 */
function parseRequestedQuantity(formData) {
  const raw = formData?.get?.('quantity');
  if (raw === null || raw === undefined || raw === '') return 1;
  const qty = parseInt(String(raw), 10);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

/**
 * Resolve the active quantity constraints from the rendered form field.
 * @param {HTMLFormElement|null|undefined} form
 * @param {HTMLElement|null|undefined} hostEl
 * @returns {{ maxQty: number|null, cartQty: number }}
 */
function resolveQuantityConstraints(form, hostEl) {
  // Quantity controls can be rendered outside the <form>; resolve the active field first.
  const quantityField = getAssociatedQuantityField(form, hostEl);
  const isSingleQuantity = hostEl?.dataset?.singleQuantity === 'true'
    || form?.closest?.('product-form')?.dataset?.singleQuantity === 'true';

  const parsePositiveIntOrNull = (value) => {
    const parsed = parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const parseNonNegativeInt = (value) => {
    const parsed = parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };

  if (!quantityField) {
    // eBooks / single-qty products still enforce max 1 even without a rendered qty field.
    return {
      maxQty: isSingleQuantity ? 1 : null,
      cartQty: 0,
    };
  }

  let maxQty = parsePositiveIntOrNull(
    quantityField.dataset?.max ?? quantityField.getAttribute?.('max'),
  );
  if (isSingleQuantity) {
    maxQty = 1;
  }
  const cartQty = parseNonNegativeInt(
    quantityField.dataset?.cartQuantity ?? quantityField.getAttribute?.('data-cart-quantity'),
  );

  return { maxQty, cartQty };
}

/**
 * Locate the active quantity field tied to this product form.
 * Handles fields rendered outside the <form> via form="<id>" linkage.
 * @param {HTMLFormElement|null|undefined} form
 * @param {HTMLElement|null|undefined} hostEl
 * @returns {HTMLElement|null}
 */
function getAssociatedQuantityField(form, hostEl) {
  const inFormField = form?.querySelector?.('[name="quantity"]');
  if (inFormField) return inFormField;

  const formId = form?.id ? String(form.id) : '';
  if (formId) {
    const escapedFormId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(formId)
      : formId;
    // Main PDP quantity <select> often uses form="<id>" rather than living inside the form.
    const linkedField = document.querySelector(`[name="quantity"][form="${escapedFormId}"]`);
    if (linkedField) return linkedField;
  }

  const scopedField = hostEl
    ?.closest?.('.js-product-info-container')
    ?.querySelector?.('.js-quantity-selector [name="quantity"]');
  return scopedField || null;
}

/**
 * Decode HTML entities that Shopify's `| json` filter may bake into locale strings
 * (e.g. `You've` → `You&#39;ve`).
 * @param {string} value
 * @returns {string}
 */
function decodeHtmlEntities(value) {
  if (typeof value !== 'string' || value === '') return value;
  if (!value.includes('&')) return value;
  const el = document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
}

/**
 * Build an error message when requested quantity exceeds limit.
 * Prefer the "already in bag" copy when the shopper already holds the max.
 * @param {number} maxQty
 * @param {{ cartQty?: number }} [opts]
 * @returns {string}
 */
function getMaxQtyExceededMessage(maxQty, { cartQty } = {}) {
  const safeMax = Number(maxQty);
  const safeCartQty = Number(cartQty);
  if (
    Number.isFinite(safeMax)
    && Number.isFinite(safeCartQty)
    && safeCartQty >= safeMax
    && window?.cartStrings?.alreadyInBagError
  ) {
    return decodeHtmlEntities(window.cartStrings.alreadyInBagError);
  }
  if (window?.cartStrings?.quantityError) {
    return decodeHtmlEntities(
      window.cartStrings.quantityError.replace('[quantity]', String(maxQty)),
    );
  }
  return `You can only add ${maxQty} of this item to your cart.`;
}

const {
  getMixedFulfillmentErrorMessage,
  hasMixedFulfillmentConflict,
} = getFulfillmentValidationUtils();

/**
 * sessionStorage-backed cart.js cache.  Survives page navigations within the
 * same tab so that navigating collection -> PDP doesn't re-fetch /cart.js.
 * Invalidated on every cartUpdate event and has a 10 s TTL as a safety net.
 *
 * An in-flight promise deduplicates concurrent fetches so that N product-form
 * elements reacting to the same event share a single /cart.js round-trip.
 */
const CART_CACHE_KEY = 'sdg:cartJsCache';
const CART_CACHE_TTL_MS = 10000;
let cartJsInflight = null;

function readCartCache() {
  try {
    const raw = sessionStorage.getItem(CART_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !Number.isFinite(parsed.ts)) return null;
    if (Date.now() - parsed.ts > CART_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCartCache(data) {
  try {
    sessionStorage.setItem(
      CART_CACHE_KEY,
      JSON.stringify({ data, ts: Date.now() }),
    );
  } catch {
    // sessionStorage full or unavailable -- silent fallback
  }
}

function clearCartCache() {
  try {
    sessionStorage.removeItem(CART_CACHE_KEY);
  } catch {
    // ignore
  }
}

function fetchCartState() {
  if (cartJsInflight) return cartJsInflight;
  cartJsInflight = fetch('/cart.js', { credentials: 'same-origin' })
    .then((r) => {
      if (!r.ok) return null;
      return r.json();
    })
    .then((data) => {
      cartJsInflight = null;
      if (data) writeCartCache(data);
      return data;
    })
    .catch(() => {
      cartJsInflight = null;
      return null;
    });
  return cartJsInflight;
}

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    clearCartCache();
  });
}

/**
 * Read the current cart quantity for a variant from /cart.js.
 * Uses a sessionStorage cache to avoid redundant fetches across page loads
 * and rapid UI interactions, plus in-flight dedup for concurrent callers.
 * Pass skipCache:true on the submit path where freshness is critical.
 * @param {number} variantId
 * @param {object} [options]
 * @param {boolean} [options.skipCache]
 * @returns {Promise<number|null>}
 */
async function getLiveCartQuantityForVariant(variantId, { skipCache = false } = {}) {
  if (!Number.isFinite(variantId) || variantId <= 0) return null;
  try {
    const cartState = (skipCache ? null : readCartCache()) || await fetchCartState();
    if (!cartState) return null;

    return getCartQuantityForVariant(cartState, variantId);
  } catch (error) {
    console.warn('product-form: failed to fetch live cart quantity', error);
    return null;
  }
}

function getCartQuantityForVariant(cartState, variantId) {
  const items = Array.isArray(cartState?.items) ? cartState.items : [];
  return items.reduce((sum, item) => {
    const itemVariantId = Number(item?.variant_id || 0);
    const itemQty = Number(item?.quantity || 0);
    if (itemVariantId !== variantId) return sum;
    return sum + (Number.isFinite(itemQty) && itemQty > 0 ? itemQty : 0);
  }, 0);
}

/**
 * Convert FormData keys like properties[Key] and attributes[Key] into objects.
 * Leaves other fields (id/quantity/etc.) to be handled explicitly.
 * @param {FormData} formData
 * @param {string} prefix
 * @returns {Record<string, string>}
 */
function getBracketedMap(formData, prefix) {
  const out = {};
  if (!formData || typeof formData.forEach !== 'function') return out;

  formData.forEach((value, key) => {
    if (!key || typeof key !== 'string') return;
    const start = `${prefix}[`;
    if (!key.startsWith(start) || !key.endsWith(']')) return;
    const innerKey = key.slice(start.length, -1);
    if (!innerKey) return;
    // FormData values are typically strings; coerce to string for safety.
    out[innerKey] = typeof value === 'string' ? value : String(value);
  });

  return out;
}

/**
 * Shared gift-card amount formatting helpers.
 * Exposed on window.SDG so other scripts can reuse exact logic.
 * @returns {{ formatCurrencyAmount: (amount: number) => string, injectFormattedAmount: (messageTemplate: string, formattedAmount: string, fallbackTemplate: string) => string }}
 */
function getGiftCardAmountUtils() {
  const root = window.SDG || (window.SDG = {});
  if (root.giftCardAmountUtils) return root.giftCardAmountUtils;

  const formatCurrencyAmount = (amount) => {
    const htmlLanguage = (document.documentElement.lang || '').toLowerCase();
    const isFrenchLocale = htmlLanguage.startsWith('fr');
    return isFrenchLocale
      ? new Intl.NumberFormat('fr-CA', {
        style: 'currency',
        currency: 'CAD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
      : `$${Number(amount).toFixed(2)}`;
  };

  const injectFormattedAmount = (messageTemplate, formattedAmount, fallbackTemplate) => {
    const baseMessage = (typeof messageTemplate === 'string' && messageTemplate.trim())
      ? messageTemplate.trim()
      : fallbackTemplate;
    if (!formattedAmount) return baseMessage;

    const amountTokenPattern = /(\s*)(?:(\$|\u20AC|\u00A3)\s*)?(\[\s*amount\s*\]|\{\{\s*amount\s*\}\})(?:\s*(\$|\u20AC|\u00A3))?/gi;
    const placeholderReplaced = baseMessage.replace(amountTokenPattern, (match, leadingWhitespace = '') => `${leadingWhitespace}${formattedAmount}`);
    if (placeholderReplaced !== baseMessage) {
      return placeholderReplaced;
    }

    const replaced = baseMessage.replace(
      /(\s*)(\$|\u20AC|\u00A3)?\s*\d+(?:[.,]\d+)?\s*(\$|\u20AC|\u00A3)?/,
      (match, leadingWhitespace = '') => `${leadingWhitespace}${formattedAmount}`,
    );
    return replaced === baseMessage ? `${baseMessage} ${formattedAmount}` : replaced;
  };

  root.giftCardAmountUtils = { formatCurrencyAmount, injectFormattedAmount };
  return root.giftCardAmountUtils;
}

// Initialize gift card amount helpers early so other product scripts can reuse them.
getGiftCardAmountUtils();

/**
 * Compute capped pickup allocation (store pickup + ship-to-store).
 * @param {object} params
 * @param {number} params.requestedQty
 * @param {number} params.storeQty
 * @param {number} params.transferDcQty
 * @returns {{ safeStore: number, safeTransfer: number, cappedRequestedQty: number, pickupQty: number, shipToStoreQty: number, shouldSplitPickup: boolean }}
 */
function computePickupSplitAllocation({ requestedQty, storeQty, transferDcQty }) {
  const req = Number(requestedQty || 0);
  const store = Number(storeQty || 0);
  const transfer = Number(transferDcQty || 0);

  const safeReq = Number.isFinite(req) && req > 0 ? req : 0;
  const safeStore = Number.isFinite(store) && store > 0 ? store : 0;
  const safeTransfer = Number.isFinite(transfer) && transfer > 0 ? transfer : 0;

  const cappedRequestedQty = Math.min(safeReq, safeStore + safeTransfer);
  const pickupQty = Math.max(0, Math.min(cappedRequestedQty, safeStore));
  const shipToStoreQty = Math.max(0, cappedRequestedQty - pickupQty);

  const shouldSplitPickup = safeStore > 0 && safeTransfer > 0 && safeReq > safeStore;

  return {
    safeStore,
    safeTransfer,
    cappedRequestedQty,
    pickupQty,
    shipToStoreQty,
    shouldSplitPickup,
  };
}

/**
 * Normalize /cart/add.js response shape for cart-notification/cart-drawer renderers.
 * Multi-item add responses may not include top-level `key`/`id`, but Dawn-style
 * renderers expect them to exist.
 * @param {any} response
 * @returns {any}
 */
function normalizeAddToCartResponse(response) {
  if (!response || typeof response !== 'object') return response;

  const firstItem = Array.isArray(response.items) ? response.items[0] : null;
  const key = response.key || firstItem?.key;
  const id = response.id || firstItem?.id;

  return {
    ...response,
    ...(key ? { key } : {}),
    ...(id ? { id } : {}),
  };
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.error = false;
        this.maxQtyValidationSeq = 0;
        this.maxQtyGuardMessage = '';
        this.isMaxQtyBlocked = false;
        if (this.form) {
          this.variantIdInput.disabled = false;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.submitButton = this.querySelector('[type="submit"]');
          this.submitButtonText = this.submitButton.querySelector('span');
          this.giftCardMessageCount();
          this.attachFieldListeners();
          this.syncGiftCardPlaceholderState();
          this.boundGiftCardPageshowHandler = () => this.syncGiftCardPlaceholderState();
          window.addEventListener('pageshow', this.boundGiftCardPageshowHandler);
          this.attachMaxQtyGuardListeners();
          this.refreshMaxQtyGuardState({ showError: false });
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      setStoreHours(hours) {
        let storeHours = null;
        const sundayHours = hours?.find((hour) => hour.key === 'hours_sunday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        const mondayHours = hours?.find((hour) => hour.key === 'hours_monday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        const tuesdayHours = hours?.find((hour) => hour.key === 'hours_tuesday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        const wednesdayHours = hours?.find((hour) => hour.key === 'hours_wednesday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        const thursdayHours = hours?.find((hour) => hour.key === 'hours_thursday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        const fridayHours = hours?.find((hour) => hour.key === 'hours_friday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        const saturdayHours = hours?.find((hour) => hour.key === 'hours_saturday')?.value?.replaceAll(' ', '') || '00:00-00:00';
        
        storeHours = {
          sunday: sundayHours,
          monday: mondayHours,
          tuesday: tuesdayHours,
          wednesday: wednesdayHours,
          thursday: thursdayHours,
          friday: fridayHours,
          saturday: saturdayHours,
        };

        return storeHours
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        // If max-qty guard has pre-blocked still surface the error
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          if (this.isMaxQtyBlocked && this.maxQtyGuardMessage) {
            this.handleErrorMessage(this.maxQtyGuardMessage);
            this.error = true;
            document.dispatchEvent(new CustomEvent('product-form:submit-error', {
              detail: { errors: this.errorMessage?.textContent || this.maxQtyGuardMessage },
            }));
          }
          return;
        }
        // Reset stale error state from prior attempts (including early-return guards)
        // so a new successful add can always publish cart updates.
        this.error = false;
        // Lock immediately to prevent rapid double-submit before async checks complete.
        this.submitButton.setAttribute('aria-disabled', true);
        const lsSelectedStore = JSON.parse(localStorage?.getItem('selectedStoreLocation') || '{}');
        let selectedStore = getSelectedStoreLocation() || lsSelectedStore;
        const $atcModal = document.querySelector('atc-modal[open]');
        const $pickupRadio = $atcModal?.querySelector('.js-atc-modal-input[value="pickup"]:checked');
        const $radioStoreName = $atcModal?.querySelector('.js-pickup-store-name');
        const radioStoreNameText = $radioStoreName?.textContent || '';
        const $pdpPickupRadio = document.querySelector('.js-fulfillment-picker-radio:checked');
        let enableEPULeadTime = null;
        let epuLeadTime = null;
        let storeHours = null;
        let timeZone = null;
        let storeShopifyLocationId = null;
        const pickupStoreTime = {
          timezone: '',
          hours: '',
        };
        
        try {
          const metaobject = await window.getShopifyMetaobjectByHandle(String(selectedStore.locationHandle));
          const fields = metaobject?.fields || null;
          enableEPULeadTime = fields?.enable_epu_lead_time || false;
          epuLeadTime = fields?.epu_lead_time || null;
          timeZone = fields?.timezone || null;
          storeHours = this.setStoreHours(fields?.storeHours);
          storeShopifyLocationId = fields?.shopify_location_id
            ? String(fields.shopify_location_id).trim()
            : null;

          if (timeZone && storeHours) {
            pickupStoreTime.timezone = timeZone;
            pickupStoreTime.hours = storeHours;
          }
        } catch (e) {
          console.error(`There was an error fetching getShopifyMetaobjectByHandle ${e}`);
        }

        // Determine which fulfillment method is selected - prioritize modal if open
        const $selectedFulfillmentRadio = $atcModal
          ? $atcModal.querySelector('.js-atc-modal-input:checked')
          : $pdpPickupRadio;
        const selectedFulfillmentMethod = $selectedFulfillmentRadio?.value || 'ship';

        const hasActiveStoreEpuLeadTime = () => {
          const epuEnabled = enableEPULeadTime === true || String(enableEPULeadTime).toLowerCase() === 'true';
          const normalizedEpuLeadTime = epuLeadTime == null ? '' : String(epuLeadTime).trim();
          return epuEnabled && normalizedEpuLeadTime !== '';
        };

        const normalizePickupFulfillmentType = (fulfillmentType) => {
          return fulfillmentType === 'pickup' && !hasActiveStoreEpuLeadTime()
            ? 'ship-to-store'
            : fulfillmentType;
        };

        // Determine fulfillment type:
        // - ship -> ship to home
        // - pickup + store has stock -> pickup (in stock at store)
        // - pickup + store has no stock but DC can transfer -> ship-to-store
        const getFulfillmentTypeFromDom = () => {
          if (selectedFulfillmentMethod === 'ship') return 'ship';

          const $typeInput = this.querySelector('.js-delivery-fulfillment-type');
          const existingType = $typeInput?.value || $typeInput?.getAttribute('value') || '';
          if (existingType === 'pickup' || existingType === 'ship-to-store') return existingType;

          // Fallback: derive from picker datasets (store/DC qty).
          const { storeQty, transferDcQty } = getInventoryContextFromDom({ atcModal: $atcModal, hostEl: this });

          if (storeQty > 0) return 'pickup';
          if (transferDcQty > 0) return 'ship-to-store';

          return 'ship-to-store';
        };

        // Validate gift card form
        if (!this.validateGiftCardForm()) {
          this.submitButton.removeAttribute('aria-disabled');
          return; // Stop submission if not valid
        }

        if (selectedFulfillmentMethod === 'pickup' && !hasCompletePickupStore(selectedStore)) {
          const canInferFromLabel = Boolean(
            $pickupRadio?.checked
            && radioStoreNameText.trim()
            && typeof window.getLocationByName === 'function',
          );

          if (!canInferFromLabel) {
            this.handleErrorMessage(window?.cartStrings?.error || 'Unable to resolve pickup store. Please reselect a store and try again.');
            this.error = true;
            this.submitButton.removeAttribute('aria-disabled');
            return;
          }

          try {
            const location = await window.getLocationByName(radioStoreNameText);
            const inferredStore = mapStockistLocationToStoreSelection(location);
            if (!hasCompletePickupStore(inferredStore)) {
              this.handleErrorMessage(window?.cartStrings?.error || 'Unable to resolve pickup store. Please reselect a store and try again.');
              this.error = true;
              this.submitButton.removeAttribute('aria-disabled');
              return;
            }

            const persistedStore = await persistSelectedStoreLocation(inferredStore);
            if (!hasCompletePickupStore(persistedStore) || persistedStore.pickupStoreOpenDaysResolved !== true) {
              this.handleErrorMessage(window?.cartStrings?.error || 'Unable to resolve pickup store. Please reselect a store and try again.');
              this.error = true;
              this.submitButton.removeAttribute('aria-disabled');
              return;
            }

            selectedStore = persistedStore;
            localStorage?.setItem('selectedStoreLocation', JSON.stringify(selectedStore));
          } catch (error) {
            console.error('product-form: failed to infer selected pickup store before add-to-cart', error);
            this.handleErrorMessage(window?.cartStrings?.error || 'Unable to resolve pickup store. Please reselect a store and try again.');
            this.error = true;
            this.submitButton.removeAttribute('aria-disabled');
            return;
          }
        }

        let selectedStorePickupOpenDays = '';
        if (selectedFulfillmentMethod === 'pickup' && hasCompletePickupStore(selectedStore)) {
          try {
            selectedStorePickupOpenDays = await resolvePickupStoreOpenDaysForStore(selectedStore);
          } catch (error) {
            console.error('product-form: failed to resolve pickup store open days before add-to-cart', error);
            this.handleErrorMessage(window?.cartStrings?.error || 'Unable to add this item to your cart. Please try again.');
            this.error = true;
            this.submitButton.removeAttribute('aria-disabled');
            return;
          }
        }

        const formData = new FormData(this.form);
        syncReleaseDateProperty(formData, { hostEl: this });
        const requestedQty = parseRequestedQuantity(formData);
        const variantId = Number(formData.get('id') || 0);
        const { maxQty, cartQty: renderedCartQty } = resolveQuantityConstraints(this.form, this);
        let liveCartState = null;
        let liveCartQty;
        try {
          liveCartState = await fetchCartState();
          liveCartQty = liveCartState ? getCartQuantityForVariant(liveCartState, variantId) : null;
        } catch (e) {
          console.error(e);
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }
        const cartQty = Number.isFinite(liveCartQty) ? liveCartQty : renderedCartQty;
        const allowedToAdd = maxQty === null ? null : Math.max(0, maxQty - cartQty);

        if (allowedToAdd !== null && requestedQty > allowedToAdd) {
          this.handleErrorMessage(getMaxQtyExceededMessage(maxQty, { cartQty }));
          this.error = true;
          document.dispatchEvent(new CustomEvent('product-form:submit-error', {
            detail: { errors: this.errorMessage?.textContent || '' },
          }));
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }

        if (hasMixedFulfillmentConflict(liveCartState, { variantId, fulfillmentMethod: selectedFulfillmentMethod })) {
          this.handleErrorMessage(getMixedFulfillmentErrorMessage());
          this.error = true;
          document.dispatchEvent(new CustomEvent('product-form:submit-error', {
            detail: { errors: this.errorMessage?.textContent || getMixedFulfillmentErrorMessage() },
          }));
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }

        this.handleErrorMessage();

        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList?.remove('hidden');

        const sections = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];
        if (this.cart) {
          formData.append(
            'sections',
            sections,
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const { storeQty, transferDcQty } = getInventoryContextFromDom({ atcModal: $atcModal, hostEl: this });
        const allocation = computePickupSplitAllocation({ requestedQty, storeQty, transferDcQty });

        if (selectedFulfillmentMethod === 'pickup' && storeShopifyLocationId) {
          const noPickupList = window?.SDG?.Data?.dcLocations?.['Retail No Pickup'];
          const isRetailNoPickup = Array.isArray(noPickupList)
            && noPickupList.some((id) => String(id).trim() === storeShopifyLocationId);
          if (isRetailNoPickup && (!hasActiveStoreEpuLeadTime() || storeQty <= 0)) {
            const oosMessage = window.productStrings?.outOfStock
              || window.productStrings?.notCurrentlyAvailableForPickup
              || 'Out of stock';
            this.handleErrorMessage(oosMessage);
            this.error = true;
            document.dispatchEvent(new CustomEvent('product-form:submit-error', {
              detail: { errors: oosMessage },
            }));
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');
            return;
          }
        }

        const shouldSplitPickup = selectedFulfillmentMethod === 'pickup' && allocation.shouldSplitPickup;

        const createBaseRequestConfig = () => {
          const cfg = fetchConfig('javascript');
          cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
          return cfg;
        };

        const submitAddToCart = () => {
          if (selectedFulfillmentMethod === 'pickup') {
            const fulfillType = normalizePickupFulfillmentType(getFulfillmentTypeFromDom());
            if (allocation.safeStore + allocation.safeTransfer > 0) {
              formData.set('quantity', String(allocation.cappedRequestedQty));
            }

            if (selectedStore?.locationId) formData.append('attributes[_fulfillment_location]', selectedStore.locationId);
            if (selectedStore?.locationName) formData.append('attributes[_fulfillment_location_name]', selectedStore.locationName);
            formData.set('properties[_fulfillment-method]', 'pickup');
            formData.set('properties[_pickup_store_open_days]', selectedStorePickupOpenDays);

            // Sets EPU Lead Time and Pickup Store Time on location change
            if (fulfillType === 'pickup') {
              if (hasActiveStoreEpuLeadTime()) {
                formData.set('properties[_epu_lead_time]', epuLeadTime);
                formData.set('properties[_fulfillment-type]', fulfillType);
                formData.set('properties[_pickup_store_time]', JSON.stringify(pickupStoreTime));
              } else {
                formData.set('properties[_epu_lead_time]', '');
                formData.set('properties[_fulfillment-type]', 'ship-to-store');
                formData.set('properties[_pickup_store_time]', '');
              }
            } else {
              formData.set('properties[_epu_lead_time]', '');
              formData.set('properties[_fulfillment-type]', fulfillType);
              formData.set('properties[_pickup_store_time]', '');
            }
          } else {
            formData.set('properties[_fulfillment-method]', 'ship');
            formData.set('properties[_fulfillment-type]', 'ship');
            formData.delete('properties[_pickup_store_open_days]');
            formData.set('properties[_pickup_store_time]', '');
            formData.set('properties[_epu_lead_time]', '');
          }

          // Signal delivery option for Elevar analytics
          var rawFulfillType = formData.get('properties[_fulfillment-type]') || 'ship';
          var delivOptMap = { pickup: 'EPU', 'ship-to-store': 'STS' };
          if (window.SDG && window.SDG.Data) {
            window.SDG.Data.pendingDeliveryOption = delivOptMap[rawFulfillType] || 'DTC';
          }

          const cfg = createBaseRequestConfig();
          // FormData body: must not send JSON content-type.
          if (cfg?.headers) delete cfg.headers['Content-Type'];
          cfg.body = formData;
          return fetch(`${routes.cart_add_url}`, cfg);
        };

        const submitSplitPickupAddToCart = () => {
          const variantId = formData.get('id');
          const pickupQty = allocation.pickupQty;
          const shipToStoreQty = allocation.shipToStoreQty;

          if (!variantId || pickupQty <= 0 || shipToStoreQty <= 0) {
            return submitAddToCart();
          }

          const baseProperties = getBracketedMap(formData, 'properties');
          const attributes = getBracketedMap(formData, 'attributes');

          if (selectedStore?.locationId) attributes._fulfillment_location = String(selectedStore.locationId);
          if (selectedStore?.locationName) attributes._fulfillment_location_name = String(selectedStore.locationName);

          // Preserve selling plan if present (subscriptions, etc.)
          const sellingPlan = formData.get('selling_plan');

          const buildItem = (quantity, fulfillmentType) => {
            const normalizedFulfillmentType = normalizePickupFulfillmentType(fulfillmentType);
            const props = {
              ...baseProperties,
              '_fulfillment-method': 'pickup',
              '_fulfillment-type': normalizedFulfillmentType,
              _pickup_store_open_days: selectedStorePickupOpenDays,
            };

            const item = {
              id: variantId,
              quantity,
              properties: props,
            };

            if (sellingPlan) item.selling_plan = sellingPlan;
            return item;
          };

          const payload = {
            items: [
              buildItem(pickupQty, 'pickup'),
              buildItem(shipToStoreQty, 'ship-to-store'),
            ],
          };

          if (sections.length) payload.sections = sections;
          payload.sections_url = window.location.pathname;
          if (attributes && Object.keys(attributes).length) payload.attributes = attributes;

          // For split adds, send JSON
          const cfg = createBaseRequestConfig();
          cfg.headers['Content-Type'] = 'application/json';
          cfg.body = JSON.stringify(payload);
          // Signal delivery option for Elevar analytics (split pickup always combines EPU + STS)
          if (window.SDG && window.SDG.Data) {
            window.SDG.Data.pendingDeliveryOption = 'EPU';
          }
          return fetch(`${routes.cart_add_url}`, cfg);
        };

        const request = shouldSplitPickup ? submitSplitPickupAddToCart() : submitAddToCart();

        request
          .then(async (response) => {
            const responseText = await response.text();
            let parsedResponse = {};

            try {
              parsedResponse = responseText ? JSON.parse(responseText) : {};
            } catch (parseError) {
              console.error('product-form: /cart/add returned invalid JSON', {
                status: response.status,
                ok: response.ok,
                bodyPreview: responseText.slice(0, 300),
              });
              throw parseError;
            }

            if (!response.ok) {
              const fallbackDescription =
                window?.cartStrings?.error || 'Unable to add this item to your cart. Please try again.';

              if (parsedResponse && typeof parsedResponse === 'object') {
                return {
                  ...parsedResponse,
                  status: parsedResponse.status || response.status,
                  description: parsedResponse.description || parsedResponse.message || fallbackDescription,
                };
              }

              return {
                status: response.status,
                description: fallbackDescription,
              };
            }

            return parsedResponse;
          })
          .then(async (rawResponse) => {
            const response = normalizeAddToCartResponse(rawResponse);
            // display the success state of the add to bag modal when it's already open (non-gift-card flows)
            if (!response?.status && $atcModal) {
              $atcModal?.showSuccessState();
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                soldOutMessage.classList.remove('hidden');
              }
              this.submitButton.setAttribute('aria-disabled', true);

              const isCartPage = window.location.pathname.includes('/cart');
              if (!isCartPage) this.submitButtonText.classList.add('hidden');
              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }

            // For gift cards: open and update the ATC modal only after a successful add-to-cart
            const modalTarget = this.submitButton?.getAttribute('data-modal-target');
            if (!this.error && modalTarget) {
              const giftCardModal = document.querySelector(modalTarget);
              if (giftCardModal && typeof giftCardModal.showSuccessState === 'function' && typeof giftCardModal.open === 'function') {
                // Use the same custom amount that was actually sent to the cart
                const customGcAmountRaw = formData.get('properties[_custom-gc-amount]');
                const customGcAmount = customGcAmountRaw ? parseFloat(customGcAmountRaw) : NaN;

                if (!Number.isNaN(customGcAmount) && customGcAmount > 0) {
                  const amountInCents = Math.round(customGcAmount * 100);
                  const htmlLanguage = (document.documentElement.lang || '').toLowerCase();
                  const isFrenchLocale = htmlLanguage.startsWith('fr');
                  const fallbackFormattedAmount = isFrenchLocale
                    ? new Intl.NumberFormat('fr-CA', {
                      style: 'currency',
                      currency: 'CAD',
                      currencyDisplay: 'narrowSymbol',
                    }).format(customGcAmount)
                    : `$${customGcAmount.toFixed(2)}`;
                  const formattedAmount = isFrenchLocale
                    ? fallbackFormattedAmount
                    : (window.Shopify && typeof window.Shopify.formatMoney === 'function'
                      ? window.Shopify.formatMoney(amountInCents)
                      : fallbackFormattedAmount);

                  giftCardModal.querySelectorAll('.js-money-price').forEach((el) => {
                    el.textContent = formattedAmount;
                  });
                }

                giftCardModal.showSuccessState();
                giftCardModal.open(false);
              }
            }

          })
          .catch((e) => {
            console.error('product-form: add-to-cart request failed', e);
            this.error = true;
            this.handleErrorMessage(
              window?.cartStrings?.error || 'Unable to add this item to your cart. Please try again.',
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);

            if (!this.error) {
              document.dispatchEvent(new CustomEvent('product-form:submit-success'));
              this.submitButton.removeAttribute('aria-disabled');
            } else {
              document.dispatchEvent(new CustomEvent('product-form:submit-error', {
                detail: { errors: this.errorMessage?.textContent || '' },
              }));

              // show error message in button text
              const initialButtonText = this.submitButtonText.innerHTML;
              this.submitButtonText.innerHTML = 'Error';
              setTimeout(() => {
                this.submitButtonText.innerHTML = initialButtonText;
                this.submitButton.removeAttribute('aria-disabled');
              }, 3000);
            }
            this.error = false;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      async refreshMaxQtyGuardState({ showError = false } = {}) {
        if (!this.form || !this.submitButton) return;

        const formData = new FormData(this.form);
        const requestedQty = parseRequestedQuantity(formData);
        const variantId = Number(formData.get('id') || 0);
        const { maxQty, cartQty: renderedCartQty } = resolveQuantityConstraints(this.form, this);
        if (maxQty === null) return;

        // Ignore stale async responses when users change quantity quickly.
        const seq = ++this.maxQtyValidationSeq;
        const liveCartQty = await getLiveCartQuantityForVariant(variantId);
        if (seq !== this.maxQtyValidationSeq) return;

        const cartQty = Number.isFinite(liveCartQty) ? liveCartQty : renderedCartQty;
        const allowedToAdd = Math.max(0, maxQty - cartQty);
        const isExceeded = requestedQty > allowedToAdd;
        const maxMessage = getMaxQtyExceededMessage(maxQty, { cartQty });

        if (isExceeded) {
          this.isMaxQtyBlocked = true;
          this.maxQtyGuardMessage = maxMessage;
          // When the form submit control is hidden (PDP uses an external ATC trigger),
          // keep it enabled so requestSubmit can still run and surface the error.
          const submitButtonsHidden = Boolean(this.querySelector('.product-form__buttons.hide'));
          if (!submitButtonsHidden) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('aria-disabled', true);
          }
          if (showError) this.handleErrorMessage(maxMessage);
          return;
        }

        if (this.isMaxQtyBlocked) {
          this.isMaxQtyBlocked = false;
          this.submitButton.removeAttribute('disabled');
          if (!this.submitButton.classList.contains('loading')) {
            this.submitButton.removeAttribute('aria-disabled');
          }
        }

        if (this.maxQtyGuardMessage && this.errorMessage?.textContent === this.maxQtyGuardMessage) {
          this.handleErrorMessage(false);
        }
        this.maxQtyGuardMessage = '';
      }

      // Validate gift card form
      validateGiftCardForm() {
        const giftCardForm = this.querySelector('.js-gift-card-form');
        if (!giftCardForm) return true;

        this.clearGiftCardErrorSummary();
        let isValid = true;

        // Validate required fields
        const requiredFields = giftCardForm.querySelectorAll('input[required]');
        requiredFields.forEach((field) => {
          if (!field.value.trim()) {
            isValid = false;
            field.classList.add('input-error');
            this.showFieldError(field);
          } else {
            field.classList.remove('input-error');
            this.removeFieldError(field);
          }
        });

        const customAmountInput = document.getElementById('customAmount');
        const selectedDenominationOption = document.querySelector('[data-gc-denomination-option]:checked');
        const shouldValidateCustomByDenomination = selectedDenominationOption?.dataset?.amount === 'custom';
        let shouldValidateCustomByVariant = false;

        if (!selectedDenominationOption) {
          const variantSelects = document.querySelector('variant-selects');
          const selectedVariant = variantSelects?.querySelector('[data-selected-variant]');
          if (selectedVariant) {
            try {
              const variantData = JSON.parse(selectedVariant.textContent);
              const variantTitle = variantData?.title?.trim()?.toLowerCase();
              shouldValidateCustomByVariant = variantTitle === 'other' || variantTitle === 'others';
            } catch (e) {
              // ignore JSON parse error
            }
          }
        }

        if (selectedDenominationOption && selectedDenominationOption.dataset.amount !== 'custom') {
          customAmountInput?.classList.remove('input-error');
          if (customAmountInput) this.removeFieldError(customAmountInput);
        }

        if (shouldValidateCustomByDenomination || shouldValidateCustomByVariant) {
          const customAmountValue = customAmountInput?.value?.trim();
          const customAmountNum = customAmountValue ? parseFloat(customAmountValue) : 0;
          // Read min and max amount from HTML attributes (single source of truth)
          const minAmount = customAmountInput ? parseFloat(customAmountInput.getAttribute('min')) || 0.01 : 0.01;
          const maxAmount = customAmountInput ? parseFloat(customAmountInput.getAttribute('max')) || 500 : 500;
          const { formatCurrencyAmount, injectFormattedAmount } = getGiftCardAmountUtils();
          const formattedMinAmount = formatCurrencyAmount(minAmount);
          const formattedMaxAmount = formatCurrencyAmount(maxAmount);

          let errorMessage = null;

          if (!customAmountInput || !customAmountValue || Number.isNaN(customAmountNum)) {
            // Empty or invalid format
            errorMessage = window.variantStrings?.giftCardErrorAmount || 'Enter a valid amount';
          } else if (customAmountNum < minAmount) {
            // Below minimum
            errorMessage = injectFormattedAmount(
              window.variantStrings?.giftCardErrorAmountMin,
              formattedMinAmount,
              `Minimum amount allowed: ${formattedMinAmount}`,
            );
          } else if (customAmountNum > maxAmount) {
            // Above maximum
            errorMessage = injectFormattedAmount(
              window.variantStrings?.giftCardErrorAmountMax,
              formattedMaxAmount,
              `Maximum amount allowed: ${formattedMaxAmount}`,
            );
          }

          if (errorMessage) {
            isValid = false;
            if (customAmountInput) {
              customAmountInput.classList.add('input-error');
              this.showFieldError(customAmountInput, errorMessage);
            }
          } else if (customAmountInput) {
            customAmountInput.classList.remove('input-error');
            this.removeFieldError(customAmountInput);
          }
        }

        // Validate email confirmation
        const recipientEmail = giftCardForm.querySelector('input[name="properties[_Recipient Email]"]');
        const confirmEmail = giftCardForm.querySelector('input[name="properties[_Confirm recipient email]"]');
        // Email format regex
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (recipientEmail
          && recipientEmail.value.trim()
          && !emailPattern.test(recipientEmail.value.trim())
        ) {
          isValid = false;
          recipientEmail.classList.add('input-error');
          this.showFieldError(recipientEmail);
        } else if (recipientEmail && recipientEmail.value.trim() !== '') {
          recipientEmail.classList.remove('input-error');
          this.removeFieldError(recipientEmail);
        }

        if (confirmEmail
          && confirmEmail.value.trim()
          && !emailPattern.test(confirmEmail.value.trim())
        ) {
          isValid = false;
          confirmEmail.classList.add('input-error');
          this.showFieldError(confirmEmail);
        } else if (confirmEmail && confirmEmail.value.trim() !== '') {
          confirmEmail.classList.remove('input-error');
          this.removeFieldError(confirmEmail);
        }

        // Validate email confirmation
        if (recipientEmail && confirmEmail) {
          if (recipientEmail.value.trim() !== confirmEmail.value.trim()) {
            isValid = false;
            confirmEmail.classList.add('input-error');
            this.showFieldError(confirmEmail);
          } else if (confirmEmail.value.trim() !== ''
            && emailPattern.test(confirmEmail.value.trim())) {
            confirmEmail.classList.remove('input-error');
            this.removeFieldError(confirmEmail);
          }
        }

        // Validate message length
        const messageInput = giftCardForm.querySelector('textarea');
        if (messageInput) {
          // Check for length
          if (messageInput.value.length > 220) {
            isValid = false;
            messageInput.classList.add('input-error');
            this.showFieldError(messageInput);
          } else {
            // Only disallow ' " < > $ and emojis
            const disallowedPattern = /['"<>$]/;
            // Use Extended_Pictographic instead of Emoji to avoid false positives (like exclamation marks)
            const emojiPattern = /\p{Extended_Pictographic}/u;
            if (disallowedPattern.test(messageInput.value)
              || emojiPattern.test(messageInput.value)) {
              isValid = false;
              messageInput.classList.add('input-error');
              this.showFieldError(messageInput);
            } else {
              messageInput.classList.remove('input-error');
              this.removeFieldError(messageInput);
            }
          }
        }

        // Hide global error wrapper if all fields are valid
        const errorWrapper = giftCardForm.querySelector('.product-form__error-message-wrapper');
        if (errorWrapper) errorWrapper.setAttribute('hidden', true);

        if (!isValid) this.showGiftCardErrorSummary();
        return isValid;
      }

      clearGiftCardErrorSummary() {
        clearTimeout(this.giftCardSummaryTimer);
        this.giftCardSummaryTimer = null;
        const summary = this.querySelector('.js-gift-card-error-summary');
        if (!summary) return;
        summary.hidden = true;
        summary.textContent = '';
        summary.removeAttribute('aria-label');
      }

      showGiftCardErrorSummary() {
        const giftCardForm = this.querySelector('.js-gift-card-form');
        const summary = giftCardForm?.querySelector('.js-gift-card-error-summary');
        const context = this.closest('.js-product-info-container') || this;
        const invalidFields = context.querySelectorAll(
          '.js-gift-card-form [aria-invalid="true"], #customAmount[aria-invalid="true"]',
        );
        if (!summary || !invalidFields?.length) return;

        clearTimeout(this.giftCardAnnouncementTimer);
        const announcer = giftCardForm.querySelector('.js-gift-card-error-announcer');
        if (announcer) announcer.textContent = '';
        const list = document.createElement('ul');
        const messages = [];
        invalidFields.forEach((field) => {
          const error = document.getElementById(`${field.id}-error`);
          if (!error) return;
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${field.id}`;
          link.tabIndex = -1;
          const label = field.getAttribute('aria-label') || field.labels?.[0]?.textContent.trim();
          link.textContent = `${label}: ${error.textContent.trim()}`;
          link.addEventListener('click', (event) => {
            event.preventDefault();
            field.focus();
          });
          messages.push(link.textContent);
          item.appendChild(link);
          list.appendChild(item);
        });
        if (!messages.length) return;
        summary.replaceChildren(list);
        // VoiceOver does not read a group's descendants just because it receives focus.
        // Give the focus target the complete error text as its accessible name.
        summary.setAttribute('aria-label', messages.join('. '));
        summary.hidden = false;
        // Focus after exposing the populated summary to the accessibility tree.
        // Keep the blur announcer suppressed while focus moves into the summary.
        this.giftCardSummaryTimer = setTimeout(() => {
          summary.focus({ preventScroll: true });
          this.giftCardSummaryTimer = null;
        }, 0);
      }

      announceGiftCardFieldError(field) {
        const error = document.getElementById(`${field.id}-error`);
        const announcer = this.querySelector('.js-gift-card-error-announcer');
        if (this.giftCardSummaryTimer || !announcer || !error
          || field.getAttribute('aria-invalid') !== 'true') return;
        const label = field.getAttribute('aria-label') || field.labels?.[0]?.textContent.trim();
        const message = `${label}: ${error.textContent.trim()}`;
        if (field.dataset.announcedError === message) return;

        clearTimeout(this.giftCardAnnouncementTimer);
        announcer.textContent = '';
        this.giftCardAnnouncementTimer = setTimeout(() => {
          if (field.getAttribute('aria-invalid') === 'true'
            && this.submitButton.getAttribute('aria-disabled') !== 'true') {
            announcer.textContent = message;
            field.dataset.announcedError = message;
          }
        }, 200);
      }

      validateGiftCardRecipientField(field, showError = false) {
        const value = field.value.trim();
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const recipientEmail = this.querySelector('input[name="properties[_Recipient Email]"]');
        const isConfirmation = field.name === 'properties[_Confirm recipient email]';
        const isValid = (!field.required || Boolean(value))
          && (field.type !== 'email' || !value || emailPattern.test(value))
          && (!isConfirmation || !recipientEmail || value === recipientEmail.value.trim());

        if (isValid) {
          this.removeFieldError(field);
          delete field.dataset.announcedError;
        } else if (showError) {
          this.showFieldError(field);
        }
        return isValid;
      }

      // Gift card message character count
      giftCardMessageCount() {
        // Live character count for gift card message
        const giftCardForm = this.querySelector('.js-gift-card-form');
        if (giftCardForm) {
          const messageInput = giftCardForm.querySelector('textarea');
          const charCount = giftCardForm.querySelector('.js-character-count');
          const charCountText = charCount.dataset.textCount;
          const maxLength = parseInt(charCount.dataset.maxLength, 10);
          if (messageInput && charCount) {
            const updateCharCount = () => {
              if (messageInput.value.length > maxLength) {
                messageInput.value = messageInput.value.slice(0, maxLength);
              }
              charCount.textContent = `${messageInput.value.length}/${maxLength} ${charCountText}`;
            };
            messageInput.addEventListener('input', updateCharCount);
            // Initialize count on load
            updateCharCount();
          }
        }
      }

      // Show Gift Card Field Error
      showFieldError(input, customMessage = null) {
        // Remove any previous error message for this input
        this.removeFieldError(input);

        // Find the error message container related to this input
        let errorContainer = null;
        // Try to find a sibling error message element
        const parentInput = input.closest('.input-placeholder');
        if (!parentInput) return;

        parentInput.classList.add('has-value');
        // Add error class to parent for red border styling
        parentInput.classList.add('error');

        if (parentInput.parentNode) {
          errorContainer = parentInput.parentNode.querySelector('.input-error-message');
        }

        if (errorContainer) {
          // Initialize original message if not already stored
          if (!errorContainer.dataset.originalMessage) {
            errorContainer.dataset.originalMessage = errorContainer.textContent;
          }
          // Use custom message if provided, otherwise restore original message
          if (customMessage) {
            errorContainer.textContent = customMessage;
          } else if (errorContainer.dataset.originalMessage) {
            errorContainer.textContent = errorContainer.dataset.originalMessage;
          }
          errorContainer.removeAttribute('hidden');
          errorContainer.style.display = 'block';
          if ((input.closest('.js-gift-card-form') || input.id === 'customAmount')
            && errorContainer.id) {
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-errormessage', errorContainer.id);
            const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/)
              .filter((id) => id && id !== errorContainer.id);
            descriptions.push(errorContainer.id);
            input.setAttribute('aria-describedby', descriptions.join(' '));
          }
        }

        input.classList.add('input-error');
      }

      // Remove Gift Card Field Error
      removeFieldError(input) {
        const parentInput = input.closest('.input-placeholder');
        if (!parentInput || !parentInput.parentNode) {
          input.classList.remove('input-error');
          return;
        }

        // Remove error class from parent to remove red border styling
        parentInput.classList.remove('error');

        // Find all error message elements in the field
        const errorMessages = parentInput.parentNode.querySelectorAll('.input-error-message');
        errorMessages.forEach((error) => {
          // Initialize original message if not already stored
          if (!error.dataset.originalMessage) {
            error.dataset.originalMessage = error.textContent;
          }
          // Restore original message if it was stored (e.g., by product-gift-card.js)
          if (error.dataset.originalMessage
            && error.textContent !== error.dataset.originalMessage) {
            error.textContent = error.dataset.originalMessage;
          }
          error.setAttribute('hidden', true);
          error.style.display = 'none';
          if ((input.closest('.js-gift-card-form') || input.id === 'customAmount') && error.id) {
            const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/)
              .filter((id) => id && id !== error.id);
            if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
            else input.removeAttribute('aria-describedby');
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-errormessage');
          }
        });
        input.classList.remove('input-error');
      }

      // Validate message textarea field
      validateMessageTextarea(textarea) {
        if (!textarea || textarea.tagName !== 'TEXTAREA' || textarea.name !== 'properties[_Message]') {
          return;
        }

        const messageValue = textarea.value;
        const maxLength = 220;

        // Check for invalid characters and emojis
        const disallowedPattern = /['"<>$]/;
        // Use Extended_Pictographic instead of Emoji to avoid false positives (like exclamation marks)
        const emojiPattern = /\p{Extended_Pictographic}/u;
        const hasInvalidChars = disallowedPattern.test(messageValue);
        const hasEmojis = emojiPattern.test(messageValue);
        const exceedsLength = messageValue.length > maxLength;

        // Get parent input-placeholder element for error styling
        const parentPlaceholder = textarea.closest('.input-placeholder');

        // Show error if invalid, remove error if valid
        if (hasInvalidChars || hasEmojis || exceedsLength) {
          textarea.classList.add('input-error');
          if (parentPlaceholder) parentPlaceholder.classList.add('error');
          this.showFieldError(textarea);
        } else {
          textarea.classList.remove('input-error');
          if (parentPlaceholder) parentPlaceholder.classList.remove('error');
          this.removeFieldError(textarea);
          delete textarea.dataset.announcedError;
        }

        // Keep floating label state in sync with final validation state
        if (parentPlaceholder) {
          const hasContent = Boolean(messageValue?.trim?.()) || parentPlaceholder.classList.contains('error');
          parentPlaceholder.classList.toggle('has-value', hasContent);
        }
      }

      syncGiftCardPlaceholderState() {
        const $giftCardForm = this.querySelector('.js-gift-card-form');
        if (!$giftCardForm) return;
        const $inputs = $giftCardForm.querySelectorAll('.input-placeholder input, .input-placeholder textarea') || [];

        $inputs?.forEach((field) => {
          const $wrapper = field.closest('.input-placeholder');
          if (!$wrapper) return;
          const hasContent = Boolean(field.value?.trim?.()) || $wrapper.classList.contains('error');

          $wrapper.classList.toggle('has-value', hasContent);
        });
      }

      // Attach event listeners to gift card form fields
      attachFieldListeners() {
        const giftCardForm = this.querySelector('.js-gift-card-form');
        if (!giftCardForm) return;

        const fields = giftCardForm.querySelectorAll('input[required], textarea, #customAmount');
        fields.forEach((field) => {
          // For message textarea, validate on all events
          if (field.tagName === 'TEXTAREA' && field.name === 'properties[_Message]') {
            // Validate on input (as user types)
            field.addEventListener('input', () => {
              this.clearGiftCardErrorSummary();
              this.validateMessageTextarea(field);
            });

            // Validate on blur (when user leaves the field)
            field.addEventListener('blur', () => {
              this.validateMessageTextarea(field);
              this.announceGiftCardFieldError(field);
            });

            // Validate on change
            field.addEventListener('change', () => {
              this.validateMessageTextarea(field);
            });

            // Validate on paste (when user pastes content)
            field.addEventListener('paste', () => {
              // Use setTimeout to validate after paste content is applied
              setTimeout(() => {
                this.validateMessageTextarea(field);
              }, 0);
            });

            // Validate on cut (when user cuts content)
            field.addEventListener('cut', () => {
              setTimeout(() => {
                this.validateMessageTextarea(field);
              }, 0);
            });
          } else {
            // Clear recipient errors only after the existing validation rules pass.
            field.addEventListener('input', () => {
              const wrapper = field.closest?.('.input-placeholder');
              if (wrapper) {
                const hasContent = Boolean(field.value?.trim?.()) || wrapper.classList.contains('error');
                wrapper.classList.toggle('has-value', hasContent);
              }
              if (field.id === 'customAmount') {
                if (field.value.trim() !== '') this.removeFieldError(field);
                return;
              }
              this.clearGiftCardErrorSummary();
              this.validateGiftCardRecipientField(field);
              const confirmEmail = giftCardForm
                .querySelector('input[name="properties[_Confirm recipient email]"]');
              if (field.name === 'properties[_Recipient Email]'
                && confirmEmail?.getAttribute('aria-invalid') === 'true') {
                this.validateGiftCardRecipientField(confirmEmail);
              }
            });

            if (field.id === 'customAmount') return;
            field.addEventListener('blur', () => {
              this.validateGiftCardRecipientField(field, true);
              this.announceGiftCardFieldError(field);
              if (field.name === 'properties[_Recipient Email]') {
                const confirmEmail = giftCardForm
                  .querySelector('input[name="properties[_Confirm recipient email]"]');
                if (confirmEmail?.value.trim()) {
                  this.validateGiftCardRecipientField(confirmEmail, true);
                  if (field.getAttribute('aria-invalid') !== 'true') {
                    this.announceGiftCardFieldError(confirmEmail);
                  }
                }
              }
            });
          }
        });
      }

      attachMaxQtyGuardListeners() {
        this.addEventListener('change', (event) => {
          if (event?.target?.classList?.contains('js-atc-modal-input')) {
            this.refreshMaxQtyGuardState({ showError: true });
          }
        });

        this.boundSubmitSuccessHandler = () => {
          this.refreshMaxQtyGuardState({ showError: false });
        };
        document.addEventListener('product-form:submit-success', this.boundSubmitSuccessHandler);

        // Listen at document-level so we catch quantity fields linked via form="<id>".
        this.boundQuantityChangeHandler = (event) => {
          if (!this.isAssociatedQuantityTarget(event?.target)) return;
          this.refreshMaxQtyGuardState({ showError: true });
        };
        this.boundQuantityInputHandler = (event) => {
          if (!this.isAssociatedQuantityTarget(event?.target)) return;
          this.refreshMaxQtyGuardState({ showError: false });
        };

        document.addEventListener('change', this.boundQuantityChangeHandler);
        document.addEventListener('input', this.boundQuantityInputHandler);
      }

      disconnectedCallback() {
        clearTimeout(this.giftCardSummaryTimer);
        clearTimeout(this.giftCardAnnouncementTimer);
        if (this.boundSubmitSuccessHandler) {
          document.removeEventListener('product-form:submit-success', this.boundSubmitSuccessHandler);
        }
        if (this.boundQuantityChangeHandler) {
          document.removeEventListener('change', this.boundQuantityChangeHandler);
        }
        if (this.boundQuantityInputHandler) {
          document.removeEventListener('input', this.boundQuantityInputHandler);
        }
        if (this.boundGiftCardPageshowHandler) {
          window.removeEventListener('pageshow', this.boundGiftCardPageshowHandler);
        }
      }

      isAssociatedQuantityTarget(target) {
        if (!(target instanceof Element)) return false;
        if (target.getAttribute('name') !== 'quantity') return false;
        if (!this.form) return false;

        // Native form association (inside form or linked by form attribute).
        if (target.form && target.form === this.form) return true;

        const targetFormId = target.getAttribute('form');
        if (targetFormId && this.form.id && targetFormId === this.form.id) return true;

        const scopedContainer = this.closest('.js-product-info-container');
        if (scopedContainer && scopedContainer.contains(target)) return true;

        return false;
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    },
  );
}
